import { useState, useEffect } from "react";
import { api } from "../lib/apiClient";
import { useAuth } from "../lib/auth";
import { Header, Footer } from "../components/Layout";
import { AuthGate } from "../components/AuthGate";
import { PushSubscribeButton } from "../components/PushSubscribeButton";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, Upload, FileText, Clock, CheckCircle2, ScanLine, ArrowRight } from "lucide-react";

export default function Vault() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [scannedDocs, setScannedDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    if (loading) return;
    if (user) {
      api.get("/vault/list").then((r) => setItems(r.data));
      api.get("/documents/history").then((r) => setScannedDocs(r.data)).catch(() => setScannedDocs([]));
    }
  }, [user, loading]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setMsg("");
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/vault/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setMsg(`✓ Preuve enregistrée. SHA-256: ${r.data.sha256.slice(0, 20)}…`);
      const list = await api.get("/vault/list");
      setItems(list.data);
    } catch (err) {
      setMsg("Erreur: " + (err.response?.data?.detail || "réessayez"));
    } finally { setUploading(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="pulse-dot"></div></div>;

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Header />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
        <div className="j-label mb-2 flex items-center gap-2"><ShieldCheck className="w-3 h-3" /> Coffre-Fort de Preuves (eIDAS-lite)</div>
        <h1 className="j-h1 font-heading">Vos preuves horodatées</h1>
        <p className="text-slate-600 mt-2 max-w-2xl">Chaque document déposé reçoit une empreinte SHA-256 unique et un horodatage UTC infalsifiable. Utile pour prouver l'existence d'un document à une date donnée.</p>

        <div className="j-card mt-6" data-testid="vault-upload-card">
          <AuthGate reason="sauvegarder vos preuves dans le Coffre-Fort">
            {(trigger) =>
              user ? (
                <label className="btn-emerald cursor-pointer inline-flex" data-testid="vault-upload-label">
                  <Upload className="w-4 h-4" />
                  {uploading ? "Enregistrement…" : "Déposer un document (PDF, image, texte)"}
                  <input type="file" className="hidden" onChange={upload} data-testid="vault-file-input" />
                </label>
              ) : (
                <button onClick={trigger} className="btn-emerald" data-testid="vault-locked-btn">
                  <Upload className="w-4 h-4" /> Déposer un document (connexion Google 1-clic)
                </button>
              )
            }
          </AuthGate>
          {msg && <div className="mt-3 text-sm text-feedback-success font-semibold" data-testid="vault-msg">{msg}</div>}
        </div>

        {user && (
          <div className="mt-6 mb-6">
            <PushSubscribeButton />
          </div>
        )}

        {user && (
          <div className="mt-6">
            <h2 className="font-heading font-black text-xl mb-3">Historique preuves ({items.length})</h2>
            {items.length === 0 && <div className="text-slate-500 text-sm">Aucune preuve enregistrée pour l'instant.</div>}
            <div className="space-y-2">
              {items.map((it) => (
                <div key={it.proof_id} className="j-card flex items-start gap-3" data-testid={`vault-item-${it.proof_id}`}>
                  <div className="w-10 h-10 rounded-lg bg-[#10B981]/10 flex items-center justify-center flex-shrink-0">
                    <FileText className="w-4 h-4 text-[#10B981]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-heading font-bold text-[#1E293B] truncate">{it.filename}</div>
                    <div className="text-xs text-slate-500 mt-1">
                      <Clock className="w-3 h-3 inline mr-1" /> {new Date(it.timestamp_utc).toLocaleString("fr-FR")} · {Math.round(it.size/1024)} Ko
                    </div>
                    <div className="text-[0.65rem] font-mono text-slate-400 mt-1 truncate">SHA-256: {it.sha256}</div>
                  </div>
                  <CheckCircle2 className="w-5 h-5 text-[#10B981] flex-shrink-0" />
                </div>
              ))}
            </div>

            <div className="flex items-baseline justify-between mt-10 mb-3">
              <h2 className="font-heading font-black text-xl flex items-center gap-2"><ScanLine className="w-5 h-5 text-emerald-600" /> Documents scannés ({scannedDocs.length})</h2>
              <button onClick={() => navigate("/timeline")} className="text-xs text-emerald-700 font-semibold hover:underline inline-flex items-center gap-1" data-testid="vault-timeline-link">
                Voir la timeline complète <ArrowRight className="w-3 h-3" />
              </button>
            </div>
            {scannedDocs.length === 0 && <div className="text-slate-500 text-sm">Aucun document scanné pour l'instant. Utilisez le <b>Scanner un document</b> sur la page d'accueil.</div>}
            <div className="space-y-2">
              {scannedDocs.map((d) => {
                const s = d.signals || {};
                return (
                  <div key={d.doc_id} className="j-card" data-testid={`scanned-doc-${d.doc_id}`}>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                        <ScanLine className="w-4 h-4 text-emerald-700" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-heading font-bold text-[#1E293B] truncate">{d.filename || s.document_type || "Document"}</div>
                        <div className="text-xs text-slate-500 mt-1">
                          <Clock className="w-3 h-3 inline mr-1" /> {new Date(d.created_at).toLocaleString("fr-FR")} · {s.document_type || "type ?"} · via {d.model || "AI"}
                        </div>
                        {s.action_summary && <div className="text-sm text-slate-700 mt-2 italic">« {s.action_summary} »</div>}
                        <div className="flex gap-2 mt-2 flex-wrap text-[0.65rem]">
                          {s.amounts?.length > 0 && <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 font-semibold">💶 {s.amounts.join(" · ")}</span>}
                          {s.deadline_days != null && <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-semibold">⏱ {s.deadline_days}j</span>}
                          {s.recommended_action && (
                            <span className={`px-2 py-0.5 rounded-full font-semibold ${
                              s.recommended_action === "lawyer" ? "bg-amber-100 text-amber-800" :
                              s.recommended_action === "urgent" ? "bg-red-100 text-red-800" :
                              "bg-emerald-100 text-emerald-800"
                            }`}>{s.recommended_action === "lawyer" ? "Avocat" : s.recommended_action === "urgent" ? "Urgent" : s.recommended_action === "self" ? "Vous seul(e)" : "Info"}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
